import React, { useEffect, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import { Tag } from "antd";
import dayjs from "dayjs";
import {
  VideoCameraOutlined,
  AudioOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Table from "../../../../../Common/Table";
import { useParams, useSearchParams } from "react-router";
import {
  RecordingGetParams,
  RecordingTableRow,
  useRecordingTableGet,
} from "../../../../../../hooks/api/recordings";
import { SortOrder } from "antd/es/table/interface";
import { RequestData } from "@ant-design/pro-components";
import { getSorterParams } from "../../../../../../utils";
import VideoPlayerModal from "../../../../../Common/VideoPlayerModal";
import { IconAudio, IconVideo } from "../../../../../Common/Icon";
import DeleteRecordingButton from "./actions";
import { useVaadTabs } from "../context";

interface RecordingsTabProps {
  activeTab?: string;
}

const TYPE_CONFIG = {
  video: {
    icon: <VideoCameraOutlined />,
    bg: "#e6f4ff",
    border: "#91caff",
    color: "#0958d9",
    text: "Video",
  },
  audio: {
    icon: <AudioOutlined />,
    bg: "#f6ffed",
    border: "#b7eb8f",
    color: "#389e0d",
    text: "Audio",
  },
};

export default function RecordingsTab({
  activeTab,
}: RecordingsTabProps): React.ReactNode | null {
  const { id } = useParams();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const recordingTableGet = useRecordingTableGet();
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
  const { selectedMiddahId, middahOptions } = useVaadTabs();
  const [currentVideo, setCurrentVideo] = useState<{
    name: string;
    fileLink: string;
  } | null>(null);

  useEffect(() => {
    if (activeTab === "recordings") {
      setTableKey(Date.now());
    }
  }, [activeTab]);

  const openVideoPlayer = (record: RecordingTableRow) => {
    if (record.fileLink) {
      setCurrentVideo({
        name: record.name,
        fileLink: record.fileLink,
      });
      setVideoPlayerOpen(true);
    }
  };

  const columns: ProColumns<RecordingTableRow>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: record.fileLink ? "pointer" : "default",
          }}
          onClick={() => record.fileLink && openVideoPlayer(record)}
        >
          {record.type === "video" ? <IconVideo /> : <IconAudio />}
          <span
            style={{
              textDecoration: record.fileLink ? "underline" : "none",
            }}
          >
            {record.name}
          </span>
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: true,
      hideInSearch: true,
      render: (_: any, record: RecordingTableRow) => {
        const config = TYPE_CONFIG[record.type];
        return (
          <Tag
            style={{
              borderRadius: 4,
              border: `1px solid ${config.border}`,
              background: config.bg,
              color: config.color,
              margin: 0,
            }}
          >
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: "Middah",
      dataIndex: "middahIds",
      key: "middahIds",
      hideInTable: true,
      sorter: true,
      valueType: "select",
      valueEnum: Object.fromEntries(
        middahOptions.map((m) => [m.value, { text: m.label }]),
      ),
      render: (_, record) => {
        const allTags = record.middahs || [];
        const visibleTags = allTags.slice(0, 3);
        const hiddenCount = allTags.length - 3;

        return (
          <span
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {visibleTags.map((tag) => (
              <span key={tag.id}>{tag.name}</span>
            ))}
            {hiddenCount > 0 && <span>+{hiddenCount}</span>}
          </span>
        );
      },
    },
    {
      title: "Middah",
      dataIndex: "middahs",
      key: "middahs",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => {
        const allTags = record.middahs || [];
        const visibleTags = allTags.slice(0, 3);
        const hiddenCount = allTags.length - 3;

        return (
          <span
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {visibleTags.map((tag) => (
              <span key={tag.id}>{tag.name}</span>
            ))}
            {hiddenCount > 0 && <span>+{hiddenCount}</span>}
          </span>
        );
      },
    },
    {
      title: "Upload Date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => {
        const date = dayjs(record.createdAt);
        return date.isValid() ? date.format("MMMM D, YYYY") : "-";
      },
    },
    {
      title: "Associated Meeting",
      dataIndex: "associatedMeeting",
      key: "associatedMeeting",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => <div>{record.meeting?.name || "-"}</div>,
    },
  ];

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number;
      current?: number;
      keyword?: string;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<RecordingTableRow>>> => {
    const newParams: RecordingGetParams = {
      vaadId: id,
      page: current || 1,
      pageSize: pageSize || 10,
      orderByColumn: "createdAt",
      orderBy: "DESC",
      ...args,
      ...getSorterParams(sorter),
    };

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return recordingTableGet.fetch(newParams).then((response) => {
      if (response) {
        const { data, total } = response;

        return {
          data: data || [],
          success: true,
          total,
        };
      }

      return { data: [], success: false, total: 0 };
    });
  };

  return (
    <>
      <Table<RecordingTableRow>
        key={`recordings-${tableKey}-${i18n.language}`}
        resetKey={() => setTableKey(Date.now())}
        request={tableRequest}
        className="tableCustomPadding"
        headerTitle={t("pages.recordings.title")}
        columns={columns}
        actions={[
          [
            DeleteRecordingButton,
            {
              setTableKey,
            },
          ],
        ]}
        search={{
          style: {
            // display: "none",
          },
        }}
        options={{
          search: {
            name: "search",
            placeholder: "Search by recording name",
            style: { width: 280 },
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-recordings",
          persistenceType: "localStorage",
        }}
      />
      <VideoPlayerModal
        open={videoPlayerOpen}
        onClose={() => {
          setVideoPlayerOpen(false);
          setCurrentVideo(null);
        }}
        videoName={currentVideo?.name}
        fileLink={currentVideo?.fileLink}
      />
    </>
  );
}
